import React from "react";
import styled from "styled-components";
import { Repeater, types } from "react-bricks/frontend";
import { color } from "styles/colours";
import {
  VerticalPadding,
  VerticalPaddingWrapper,
  verticalPadSideProp,
} from "../utils/verticalPadding";
import { LogoWallImageProps } from "./LogoWallImage";
import TextBlock from "../common/TextBlock";
import { useIsBelowBreakpoint } from "../utils/isBelowBreakpoint";

enum LogoSize {
  small = 90,
  medium = 150,
  large = 188,
}

interface LogoWallProps {
  background?: types.IColor;
  paddingVertical?: VerticalPadding;
  items: types.RepeaterItems<LogoWallImageProps>;
  logoSize: LogoSize;
  fontColor?: types.IColor;
  alignment: "spaced" | "center";
}

export const LogoWall: types.Brick<LogoWallProps> = ({
  paddingVertical,
  background = { color: color.white },
  items,
  logoSize = LogoSize.large,
  fontColor = { color: color.B1100 },
  alignment = "center",
}) => {
  const belowBreakpoint = useIsBelowBreakpoint(1200);
  const adjustedLogoSize = belowBreakpoint ? 150 : logoSize;

  return (
    <VerticalPaddingWrapper paddingVertical={paddingVertical} backgroundColor={background?.color}>
      <Wrapper>
        <TitleWrapper fontColor={fontColor.color}>
          <TextBlock
            propName="title"
            placeholder="Loved by over 1,000,000 employees across thousands of companies:"
            renderBlock={({ children }) => <>{children}</>}
          />
        </TitleWrapper>

        <LogoWrapper alignment={alignment}>
          <Repeater
            propName="items"
            items={items}
            itemProps={{
              maxWidth: adjustedLogoSize,
            }}
          />
        </LogoWrapper>
      </Wrapper>
    </VerticalPaddingWrapper>
  );
};

LogoWall.schema = {
  name: "logo-wall",
  label: "Logo Wall",
  category: "Logo Wall Components",
  previewImageUrl: "/static/bricks/logo-wall.png",
  // Define repeaters for child bricks
  repeaterItems: [
    {
      name: "items",
      itemType: "logo-wall-image",
      itemLabel: "Logos",
      min: 1,
      max: 7,
    },
  ],
  // Define default props
  getDefaultProps: () => ({
    background: { color: color.white },
    paddingVertical: "s",
    items: [],
    logoSize: LogoSize.large,
    title: "Loved by over 1,000,000 employees across thousands of companies:",
    fontColor: { color: color.B1100 },
    alignment: "center",
  }),
  // Define side edit props
  sideEditProps: [
    verticalPadSideProp,
    {
      name: "background",
      label: "Background Color",
      type: types.SideEditPropType.Select,
      shouldRefreshStyles: true,
      selectOptions: {
        display: types.OptionsDisplay.Color,
        options: [
          { label: "Purple", value: { color: color.S3100 } },
          { label: "Pink", value: { color: color.S310 } },
          { label: "Green", value: { color: color.S110 } },
          { label: "Yellow", value: { color: color.S410 } },
          { label: "Blue", value: { color: color.S210 } },
          { label: "White", value: { color: color.white } },
          { label: "Gray", value: { color: color.B210 } },
        ],
      },
    },
    {
      name: "logoSize",
      label: "Logo Size",
      shouldRefreshStyles: true,
      type: types.SideEditPropType.Select,
      selectOptions: {
        display: types.OptionsDisplay.Radio,
        options: [
          { value: LogoSize.small, label: "Small" },
          { value: LogoSize.medium, label: "Medium" },
          { value: LogoSize.large, label: "Large" },
        ],
      },
    },
    {
      name: "fontColor",
      label: "Font Color",
      type: types.SideEditPropType.Select,
      shouldRefreshStyles: true,
      selectOptions: {
        display: types.OptionsDisplay.Color,
        options: [
          { value: { color: color.B1100 }, label: "Black" },
          { value: { color: color.white }, label: "White" },
        ],
      },
    },
    {
      name: "alignment",
      label: "Alignment",
      type: types.SideEditPropType.Select,
      selectOptions: {
        display: types.OptionsDisplay.Radio,
        options: [
          { value: "spaced", label: "Spaced" },
          { value: "center", label: "Center" },
        ],
      },
    },
  ],
};

const Wrapper = styled.div`
  margin: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TitleWrapper = styled.div<{ fontColor: string }>`
  display: flex;
  justify-content: center;
  text-align: center;
  color: ${(p) => p.fontColor};
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  padding: 0 2rem;
`;

const LogoWrapper = styled.div<{ alignment: "spaced" | "center" }>`
  width: 100%;
  display: flex;
  justify-content: ${(p) => (p.alignment === "spaced" ? "space-between" : "center")};
  gap: 2.5rem;
  padding: 0 5rem;

  @media only screen and (max-width: 75rem) {
    justify-content: center;
    flex-wrap: wrap;
    gap: 1.5rem 2.5rem;
    padding: 0 0.5rem;
  }
`;
